/**
 * The edit sheet's suggestion logic, as plain functions.
 *
 * ── WHY THESE ARE NOT IN THE SHEET ──
 * "Preselect Travel" is a decision the sheet makes on the person's behalf,
 * and a wrong one is worse than none: a category set without anybody
 * choosing it, on a row that is about to be confirmed in one press with 24
 * others. So the rule for WHEN it fires is a function Node's test runner can
 * pin - as `inbox-books.ts` keeps the cross-book grouping out of the page -
 * and the sheet only draws what these return.
 */

export interface SuggestedCategory {
  id: string;
  name: string;
  emoji: string | null;
  colour: string;
  count: number;
  lastDate: string;
}

export interface SuggestedAccount {
  id: string;
  name: string;
  count: number;
}

export interface Suggestions {
  payee: string | null;
  payeeKey: string | null;
  descriptions: string[];
  categories: SuggestedCategory[];
  accounts: SuggestedAccount[];
}

export const EMPTY_SUGGESTIONS: Suggestions = {
  payee: null,
  payeeKey: null,
  descriptions: [],
  categories: [],
  accounts: [],
};

export type SuggestType = 'expense' | 'deposit';

/**
 * The category a new book files everything under until somebody says
 * otherwise - and what a model puts on a row it could not place. A row
 * still on it has not been decided, so a suggestion may decide it; a row
 * on anything else was chosen, and a suggestion is only offered.
 */
export const SEEDED_DEFAULT: Record<SuggestType, string> = {
  expense: 'Other expenses',
  deposit: 'Other income',
};

/** Whether the row's category is still the undecided one - the seeded default, or nothing at all. */
export function isUndecided(type: SuggestType, categoryName: string | null | undefined): boolean {
  if (!categoryName) return true;
  return categoryName.trim().toLowerCase() === SEEDED_DEFAULT[type].toLowerCase();
}

/**
 * The one category the history points at, or null when it does not point.
 *
 * Ranked already (count, then recency), so the question is only whether the
 * first is clear of the second: used at least twice, and at least twice as
 * often as anything else. Three rides and one lunch is an answer; two and
 * two is a question, and the chips ask it.
 */
export function unambiguousCategory(categories: SuggestedCategory[]): SuggestedCategory | null {
  const [top, next] = categories;
  if (!top || top.count < 2) return null;
  if (next && top.count < next.count * 2) return null;
  return top;
}

export interface BatchRow {
  type: string;
  id: string;
  payeeKey: string | null;
  categoryId: string | null;
}

/**
 * The batch's OTHER rows for the same payee and of the same type - the ones
 * "Also file N other rows" would file. Not the row being edited; not a row
 * whose description names nobody, which would match every other such row;
 * and only the same record type, because an expense category is nothing on
 * a deposit.
 */
export function batchRowsForPayee<R extends BatchRow>(rows: R[], editing: { type: string; id: string }, payeeKey: string | null): R[] {
  if (!payeeKey) return [];
  return rows.filter(
    (row) => row.type === editing.type && row.id !== editing.id && row.payeeKey === payeeKey,
  );
}

/** "Suggested from 3 earlier payments to Mahendiran A" */
export function suggestedLine(type: SuggestType, count: number, payee: string): string {
  const noun = type === 'expense' ? (count === 1 ? 'payment to' : 'payments to') : count === 1 ? 'deposit from' : 'deposits from';
  return `Suggested from ${count} earlier ${noun} ${payee}`;
}

/** "Also file 1 other row from Mahendiran A in this batch under ✈️ Travel" */
export function applyToBatchLabel(count: number, payee: string, categoryLabel: string): string {
  return `Also file ${count} other ${count === 1 ? 'row' : 'rows'} from ${payee} in this batch under ${categoryLabel}`;
}

/**
 * The sentence above the list once the sheet has saved: what was filed
 * alongside, and what could not be.
 */
export function savedNotice(applied: number, failed: number, categoryLabel: string): string {
  if (applied === 0 && failed === 0) return 'Saved.';
  const head = `Saved. Also filed ${applied} other ${applied === 1 ? 'row' : 'rows'} under ${categoryLabel}.`;
  return failed ? `${head} ${failed} could not be - the reason is beside each one.` : head;
}
